import { useTranslation } from 'react-i18next';
import { Flex } from '@chakra-ui/react';

import type { ApiNabanDeed } from '@izimi/api';
import { useIsMobile } from '@izimi/hooks';

import { NabanCardListItem } from '../../../components/NabanCardListItem';
import { NabanCardListItemTitle } from '../../../components/NabanCardListItemTitle';
import { NabanDetailsListCard } from '../../../components/NabanDetailsListCard';
import { NabanDetailsListItem } from '../../../components/NabanDetailsListItem';
import { NabanItemBadge } from '../../../components/NabanItemBadge';
import { useNabanStoreSelectedAuthenticationMethod } from '../../../store';
import type { RenderDeedOptions } from '../../util/types';
import { nabanDetailsListCardContentProps } from './styles';

type Props = {
  financeDocuments: ApiNabanDeed['financeDocuments'];
  justiceDocuments: ApiNabanDeed['justiceDocuments'];
  napId: string;
  renderDeedOptions?: RenderDeedOptions;
};

export const NabanDeedSentDocuments = ({
  justiceDocuments,
  financeDocuments,
  napId,
  renderDeedOptions,
}: Props) => {
  const isMobile = useIsMobile();
  const { t } = useTranslation('naban');

  const selectedAuthenticationMethod = useNabanStoreSelectedAuthenticationMethod();

  const documents = [
    ...justiceDocuments.map((doc) => ({ ...doc, type: 'justice' as const })),
    ...financeDocuments.map((doc) => ({ ...doc, type: 'finance' as const })),
  ];

  return (
    <NabanDetailsListCard
      title={t('deed.sentDocuments.title')}
      contentProps={nabanDetailsListCardContentProps}>
      {documents.map((doc) => {
        const origins: Record<typeof doc.type, string> = {
          finance: t('deed.sentDocuments.fpsFinance'),
          justice: t('deed.sentDocuments.fpsJustice'),
        };

        return (
          <NabanCardListItem
            key={doc.documentToken}
            title={
              doc.title ? (
                <Flex gap={4} alignItems='center'>
                  <NabanCardListItemTitle title={doc.title} />
                  <Flex flex={1} alignItems='center'>
                    {!isMobile && doc.documentType && (
                      <NabanItemBadge label={doc.documentType.label} />
                    )}
                  </Flex>
                </Flex>
              ) : (
                ''
              )
            }>
            <NabanDetailsListItem
              flex={1}
              title={t('deed.sentDocuments.label.destination')}
              value={origins[doc.type]}
              display={isMobile ? 'none' : 'flex'}
            />
            {renderDeedOptions
              ? renderDeedOptions({
                  documentToken: doc.documentToken,
                  documentTitle: doc.documentType?.label ?? '',
                  napId,
                  documentIdentifier: doc.documentIdentifier,
                  authenticationModeToken: selectedAuthenticationMethod?.token,
                  isEmbeddedDocument: false,
                })
              : null}
          </NabanCardListItem>
        );
      })}
    </NabanDetailsListCard>
  );
};
